<template>
    <ReaderFullViewContentModal v-if="show" dusk="ReaderThumbnails">
        <DynamicScroller
            v-if="publication.files_info.length > 0"
            ref="scroller"
            :items="thumbsGroups"
            key-field="groupIndex"
            :min-item-size="350"
            class="h-full px-4 py-16"
            :style="heightStyle"
        >
            <template #default="{ item, index, active }">
                <DynamicScrollerItem
                    :item="item"
                    :active="active"
                    :data-index="index"
                    :class="['grid', 'gap-4', 'my-2', twGridClass]"
                >
                    <div
                        v-for="(file, fileIndex) in item.groupItems"
                        :key="fileIndex"
                        class="mb-8 flex flex-col items-center justify-center"
                    >
                        <button
                            type="button"
                            :aria-label="
                                (localizations[lang].page || 'Page') + ' ' + (item.groupIndex * columns + fileIndex + 1)
                            "
                            class="flex h-full w-full flex-col items-center justify-center rounded border border-theme-border p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                            :class="{
                                'border-theme-icon bg-theme-icon-bg': currentPages.includes(
                                    item.groupIndex * columns + fileIndex + 1
                                ),
                                'hover:border-theme-icon': !currentPages.includes(
                                    item.groupIndex * columns + fileIndex + 1
                                ),
                            }"
                            @click="goToThumb({ page: item.groupIndex * columns + fileIndex + 1 })"
                        >
                            <div class="relative aspect-[3/4] w-full">
                                <img
                                    :ref="'thumbnail_' + (item.groupIndex * columns + fileIndex + 1)"
                                    v-lazy="getThumb(file)"
                                    :data-page="item.groupIndex * columns + fileIndex + 1"
                                    class="absolute inset-0 h-full w-full object-contain opacity-40 transition-opacity duration-300"
                                    :class="{ 'opacity-100': loaded[item.groupIndex * columns + fileIndex + 1] }"
                                    draggable="false"
                                    :alt="
                                        (localizations[lang].page || 'Page') +
                                        ' ' +
                                        (item.groupIndex * columns + fileIndex + 1)
                                    "
                                    @load="onImageLoad(item.groupIndex * columns + fileIndex + 1)"
                                />
                            </div>
                            <span class="mt-2 min-h-6 text-theme-icon">
                                {{ item.groupIndex * columns + fileIndex + 1 }}
                            </span>
                        </button>
                    </div>
                </DynamicScrollerItem>
            </template>
        </DynamicScroller>
    </ReaderFullViewContentModal>
</template>

<script>
'use strict';

import { mapActions, mapGetters, mapState } from 'vuex';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import chunk from 'lodash/chunk';
import ReaderFullViewContentModal from '../ReaderModal/ReaderFullViewContentModal.vue';

export default {
    name: 'ReaderSidebarThumbnails',
    components: {
        DynamicScroller,
        DynamicScrollerItem,
        ReaderFullViewContentModal,
    },
    data() {
        return {
            readerToolbarHeight: 55,
            loaded: {},
        };
    },
    computed: {
        ...mapState(['device', 'publication', 'ui', 'localizations', 'lang', 'reader']),
        ...mapGetters(['currentPage', 'currentPages']),
        show() {
            return this.ui.thumbnails.opened && this.ui.thumbnails.enabled;
        },
        thumbsGroups() {
            return chunk(this.publication.files_urls, this.columns).map((group, index) => {
                return {
                    groupIndex: index,
                    groupItems: group,
                };
            });
        },
        heightStyle() {
            return `height: calc(100% - ${this.ui.headerbar.offsetHeight + 20}px)`;
        },
        columns() {
            // console.log(this.device.width)
            if (this.device.width >= 1024) {
                return 9;
            }
            if (this.device.width >= 980) {
                return 8;
            }
            /* Tablet */
            if (this.device.width >= 760) {
                return 6;
            }
            /* Mobile HD */
            if (this.device.width >= 350) {
                return 3;
            }
            /* Mobile LD */
            return 2;
        },
        twGridClass() {
            const classes = {
                9: 'grid-cols-9',
                8: 'grid-cols-8',
                6: 'grid-cols-6',
                3: 'grid-cols-3',
                2: 'grid-cols-2',
            };

            return classes[this.columns];
        },
    },
    watch: {
        show(value) {
            if (value) {
                this.readerToolbarHeight = document.querySelector('.ReaderFooter')
                    ? document.querySelector('.ReaderFooter').offsetHeight
                    : this.readerToolbarHeight;
                this.scrollToThumbnail();
            }
        },
    },
    methods: {
        ...mapActions(['goToPage', 'toggleThumbnails']),
        scrollToThumbnail() {
            const targetGrupIndex = Math.floor(
                this.currentPage / this.columns - (this.currentPage % this.columns === 0 ? 1 : 0)
            );
            this.$nextTick(() => {
                const scroller = this.$refs.scroller;
                // Check if scroller exists, index is valid, and we have enough thumbnail groups
                // This prevents crashes when the virtual scroller hasn't fully initialized yet
                if (scroller && targetGrupIndex >= 0 && this.thumbsGroups.length > targetGrupIndex) {
                    try {
                        // Virtual scroller may throw if sizes array isn't ready yet
                        scroller.scrollToItem(targetGrupIndex);
                    } catch (error) {
                        console.warn('Error scrolling to thumbnail:', error);
                    }
                }
            });
        },
        getThumb(file) {
            // Ensure we always return a valid URL or the loading placeholder
            if (!file) {
                return 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            }
            return (
                file.thumb ||
                file.large ||
                'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
            );
        },
        goToThumb(page) {
            this.goToPage(page);
            this.toggleThumbnails();
        },
        onImageLoad(pageNumber) {
            this.loaded = { ...this.loaded, [pageNumber]: true };
        },
    },
};
</script>

<style scoped>
img[lazy='loaded'] {
    @apply opacity-100;
}
</style>
